import React, { useState, useEffect } from 'react';
import { userService } from '../../../_services';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from '@/components/ui/pagination';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, Search, ArrowLeft, Landmark, FileKey } from 'lucide-react';

// ─── User type → badge variant (mirrors Commission Tracking Type column) ──────
function getUserTypeVariant(userType) {
    return 'default';
}

// ─── Mobile card ─────────────────────────────────────────────────────────────
function WiseW9Card({ report }) {
    const hasWise = !!report.accountInfo?.accountType;
    const hasW9   = !!report.w9Info?.signed_Date;

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                {/* Header: name + user-type badge */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{report.userName || '-'}</p>
                        <p className="text-xs text-muted-foreground truncate">{report.fullName || '-'}</p>
                    </div>
                    <Badge variant={getUserTypeVariant(report.userType)} className="shrink-0 font-normal">{report.userType || 'User'}</Badge>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3">
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium mt-0.5 truncate">{report.email || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium mt-0.5">{report.phone || '-'}</p>
                    </div>
                </div>

                {/* Wise + W-9 icons with popovers */}
                <div className="flex items-center gap-4 border-t pt-3">
                    {/* Wise */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Wise:</span>
                        {hasWise ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-accent">
                                        <Landmark className="h-5 w-5" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72" align="start">
                                    <div className="space-y-2 text-sm">
                                        <h4 className="font-semibold text-primary mb-2">Wise Account</h4>
                                        <div className="space-y-1.5">
                                            {[
                                                ['Acc Summary',    report.accountInfo.accSummary],
                                                ['Long Summary',   report.accountInfo.longAccSummary],
                                                ['Account Type',   report.accountInfo.accountType],
                                                ['Wise User Id',   report.accountInfo.wiseUserId],
                                                ['Bank',           report.accountInfo.bankName],
                                                ['Submitted',      report.accountInfo.wiseSubmitionDate],
                                            ].map(([label, val]) => (
                                                <div key={label} className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-2">
                                                    <span className="text-muted-foreground shrink-0">{label}:</span>
                                                    <span className="font-medium sm:text-right">{val || 'N/A'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <span className="text-xs text-muted-foreground">N/A</span>
                        )}
                    </div>

                    {/* W-9 */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">W-9:</span>
                        {hasW9 ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-accent">
                                        <FileKey className="h-5 w-5" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72" align="start">
                                    <div className="space-y-2 text-sm">
                                        <h4 className="font-semibold text-primary mb-2">W-9 Tax Info</h4>
                                        <div className="space-y-1.5">
                                            {[
                                                ['Signed Date',    report.w9Info.signed_Date],
                                                ['Business Class', report.w9Info.businessClassification],
                                                ['Entry Status',   report.w9Info.entryStatus],
                                                ['TIN',            report.w9Info.tin],
                                                ['Full Address',   report.w9Info.fullAddress],
                                            ].map(([label, val]) => (
                                                <div key={label} className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-2">
                                                    <span className="text-muted-foreground shrink-0">{label}:</span>
                                                    <span className="font-medium sm:text-right break-words">{val || 'N/A'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <span className="text-xs text-muted-foreground">N/A</span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * Wise & W-9 Report
 * Modernized admin report for viewing Wise account and W-9 tax status
 */
function WiseW9Report({ hideWiseW9Section }) {
    const [reportList, setReportList] = useState([]);
    const [reportListTemp, setReportListTemp] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageOfItems, setPageOfItems] = useState([]);

    // Fetch reports on mount
    useEffect(() => {
        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        const userName = usermetaData.split(':')[1];
        fetchReports(userId, userName);
    }, []);

    const fetchReports = async (userId, userName) => {
        setLoading(true);
        try {
            if (userService.GetUserFinInfo) {
                const apidata = await userService.GetUserFinInfo(userId, userName);

                if (apidata && apidata.length > 0) {
                    setReportList(apidata);
                    setReportListTemp(apidata);
                } else {
                    setReportList([]);
                    setReportListTemp([]);
                }
            } else {
                console.warn('GetUserFinInfo API not available');
                setReportList([]);
                setReportListTemp([]);
            }
        } catch (err) {
            console.error('Error fetching Wise & W-9 reports:', err);
            setReportList([]);
            setReportListTemp([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle search filter
    const handleSearch = (value) => {
        setSearchText(value);

        if (!value || value.length === 0) {
            setReportList(reportListTemp);
            return;
        }

        const searchLower = value.toLowerCase();
        const filtered = reportListTemp.filter(report => {
            return (
                (report.email && report.email.toLowerCase().includes(searchLower)) ||
                (report.fullName && report.fullName.toLowerCase().includes(searchLower)) ||
                (report.userName && report.userName.toLowerCase().includes(searchLower)) ||
                (report.phone && report.phone.toLowerCase().includes(searchLower)) ||
                (report.userType && report.userType.toLowerCase().includes(searchLower)) ||
                (report.accountInfo?.accountType && report.accountInfo.accountType.toLowerCase().includes(searchLower)) ||
                (report.accountInfo?.bankName && report.accountInfo.bankName.toLowerCase().includes(searchLower)) ||
                (report.w9Info?.entryStatus && report.w9Info.entryStatus.toLowerCase().includes(searchLower))
            );
        });

        setReportList(filtered.length > 0 ? filtered : []);
    };

    // Render Wise Details cell — bank icon with tooltip if accountInfo exists
    const renderWiseDetails = (accountInfo) => {
        if (!accountInfo?.accountType) return <span className="text-primary/50">N/A</span>;
        return (
            <TooltipProvider delayDuration={100}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-accent">
                            <Landmark className="h-5 w-5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs text-left space-y-1 p-3">
                        <p><span className="font-semibold">Acc Summary:</span> {accountInfo.accSummary || 'N/A'}</p>
                        <p><span className="font-semibold">Long Summary:</span> {accountInfo.longAccSummary || 'N/A'}</p>
                        <p><span className="font-semibold">Account Type:</span> {accountInfo.accountType || 'N/A'}</p>
                        <p><span className="font-semibold">Wise User Id:</span> {accountInfo.wiseUserId || 'N/A'}</p>
                        <p><span className="font-semibold">Bank:</span> {accountInfo.bankName || 'N/A'}</p>
                        <p><span className="font-semibold">Submission Date:</span> {accountInfo.wiseSubmitionDate || 'N/A'}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    // Render W-9 Details cell — file key icon with tooltip if w9Info exists
    const renderW9Details = (w9Info) => {
        if (!w9Info?.signed_Date) return <span className="text-primary/50">N/A</span>;
        return (
            <TooltipProvider delayDuration={100}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-accent">
                            <FileKey className="h-5 w-5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs text-left space-y-1 p-3">
                        <p><span className="font-semibold">Signed Date:</span> {w9Info.signed_Date || 'N/A'}</p>
                        <p><span className="font-semibold">Business Classification:</span> {w9Info.businessClassification || 'N/A'}</p>
                        <p><span className="font-semibold">Entry Status:</span> {w9Info.entryStatus || 'N/A'}</p>
                        <p><span className="font-semibold">TIN:</span> {w9Info.tin || 'N/A'}</p>
                        <p><span className="font-semibold">Full Address:</span> {w9Info.fullAddress || 'N/A'}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    return (
        <div className="w-full space-y-6">
            {/* Back Button */}
            <Button variant="outline" onClick={hideWiseW9Section} className="self-start">
                <ArrowLeft className="h-4 w-4" />
                Back to Reports
            </Button>

            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Wise & W-9 Report</h1>

            {/* Search Filter */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1 sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search by name, email, phone, or status..."
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                disabled={loading}
                                className="pl-9 pr-9"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Desktop table (md+) ───────────────────────────────────────────── */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="text-primary font-semibold">User Name</TableHead>
                                    <TableHead className="text-primary font-semibold">Full Name</TableHead>
                                    <TableHead className="text-primary font-semibold">Email</TableHead>
                                    <TableHead className="text-primary font-semibold">Phone</TableHead>
                                    <TableHead className="text-primary font-semibold">User Type</TableHead>
                                    <TableHead className="text-primary font-semibold">Wise Details</TableHead>
                                    <TableHead className="text-primary font-semibold">W-9 Details</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="h-8 w-8 animate-spin text-accent" />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (searchText ? reportList : pageOfItems).length > 0 ? (
                                    (searchText ? reportList : pageOfItems).map((report, index) => (
                                        <TableRow key={index} className="hover:bg-muted">
                                            <TableCell className="text-primary">{report.userName || '-'}</TableCell>
                                            <TableCell className="text-primary">{report.fullName || '-'}</TableCell>
                                            <TableCell className="text-primary">{report.email || '-'}</TableCell>
                                            <TableCell className="text-primary">{report.phone || '-'}</TableCell>
                                            <TableCell className="text-primary">
                                                <Badge variant={getUserTypeVariant(report.userType)} className="font-normal">{report.userType || 'User'}</Badge>
                                            </TableCell>
                                            <TableCell>{renderWiseDetails(report.accountInfo)}</TableCell>
                                            <TableCell>{renderW9Details(report.w9Info)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-primary/70">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {!searchText && reportList.length > 0 && (
                        <div className="p-4 border-t">
                            <Pagination
                                items={reportList}
                                onChangePage={setPageOfItems}
                                pageSize={10}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── Mobile cards (<md) ────────────────────────────────────────────── */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <Card>
                        <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : (searchText ? reportList : pageOfItems).length > 0 ? (
                    (searchText ? reportList : pageOfItems).map((report, index) => (
                        <WiseW9Card key={index} report={report} />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center p-4 py-8 sm:p-6 sm:py-8 text-primary/70">
                            No records found
                        </CardContent>
                    </Card>
                )}

                {!searchText && reportList.length > 0 && (
                    <div className="pt-2">
                        <Pagination
                            items={reportList}
                            onChangePage={setPageOfItems}
                            pageSize={10}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default WiseW9Report;
